// The Feedback dialog (T-427, US-190) from the avatar menu: an idea, a problem or praise, sent
// with the page the person is on. Only the sender and FRAME60's staff read it.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { api, problemText } from "../api";
import type { components } from "../api-schema";
import { t, type MessageKey } from "../i18n";
import { ConfirmDialog, useToast } from "./overlay";
import { Segmented } from "./ui";

type MyFeedback = components["schemas"]["MyFeedback"];
type Kind = "idea" | "problem" | "praise";

export function FeedbackDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [kind, setKind] = useState<Kind>("idea");
  const [body, setBody] = useState("");
  const send = useMutation({
    mutationFn: () => api.post<MyFeedback>("/v1/feedback", { kind, body, page: location.pathname }),
    onSuccess: () => {
      toast(t("feedback.sent"), "success");
      setBody("");
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["feedback"] });
    },
    onError: (e) => toast(problemText(e), "error"),
  });
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("feedback.title")}
      confirmLabel={t("feedback.send")}
      busy={send.isPending}
      onConfirm={() => (body.trim().length >= 3 ? send.mutate() : toast(t("error.feedback_short"), "error"))}
      body={
        <div className="flex flex-col gap-3 text-ink">
          <Segmented label={t("feedback.kind")} value={kind} onChange={setKind}
            options={(["idea", "problem", "praise"] as Kind[]).map((v) => ({ value: v, label: t(`feedback.kind.${v}` as MessageKey) }))} />
          <textarea
            rows={5}
            maxLength={4000}
            className="w-full rounded-[7px] border border-rule bg-ground px-3 py-2 text-[15px]"
            aria-label={t("feedback.body")}
            placeholder={t(`feedback.placeholder.${kind}` as MessageKey)}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <p className="m-0 text-xs text-steel">
            {t("feedback.privacy")}{" "}
            <Link to="/feedback" onClick={() => onOpenChange(false)} className="text-blue underline underline-offset-2">
              {t("feedback.board")}
            </Link>
          </p>
        </div>
      }
    />
  );
}
